import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { fetchVenuesByLocation } from "../helpers/onCallFunctions.ts";
import { LocationSearchBar } from "../components/SearchBars/LocationSearchBar.tsx";
import { WhatsOnButton } from "../components/Buttons/WhatsOnButton.tsx";
import { StandardTextBlock } from "../components/StandardTextBlock/StandardTextBlock.tsx";
import { LinkToAboutPage } from "../components/TextLinks/LinkToAboutPage.tsx";
import { VenueFormGroup } from "../components/VenueFormGroup/VenueFormGroup.tsx";
import { VenueSelectorPageHead } from "../components/Heads/VenueSelectorHead.tsx";
import { ChildVenue } from "../types/dataTypes.ts";
import { LoadingElementInBox } from "../components/Loading/LoadingElement.tsx";

export const VenueSelectorPage: React.FC = () => {
  const [selectedVenues, setSelectedVenues] = useState<string[]>([]);
  const [venuesNearLocation, setVenuesNearLocation] = useState<ChildVenue[]>(
    [],
  );
  const [isLocationCurrentlySelected, setIsLocationCurrentlySelected] =
    useState<boolean>(false);
  const [hasUserSelectedLocation, setHasUserSelectedLocation] =
    useState<boolean>(false);
  const [hasUserSelectedSearchBar, setHasUserSelectedSearchBar] =
    useState<boolean>(false);
  const [showLandingPageText, setShowLandingPageText] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [isBootstrappingPage, setIsBootstrappingPage] = useState(true);
  const isMobile = useMediaQuery("(max-width:600px)");

  const handleLocationSelect = async (latitude: number, longitude: number) => {
    try {
      setHasUserSelectedLocation(true);
      setLoading(true);
      const fetchedVenues = await fetchVenuesByLocation({
        latitude,
        longitude,
      });
      setVenuesNearLocation(fetchedVenues);
      setSelectedVenues([]);
      setIsLocationCurrentlySelected(true);
      setLoading(false);
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  const handleLocationClear = async () => {
    setSelectedVenues([]);
    setVenuesNearLocation([]);
    setIsLocationCurrentlySelected(false);
  };

  const handleVenueCheckboxClick = (clickedVenue: string) => {
    setSelectedVenues((prev) => {
      const isSelected = prev.includes(clickedVenue);
      return isSelected
        ? prev.filter((v) => v !== clickedVenue)
        : [...prev, clickedVenue];
    });
  };

  const toggleSelectAllVenues = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedVenues(venuesNearLocation.map((v) => v.venue_slug));
    } else {
      setSelectedVenues([]);
    }
  };

  React.useEffect(() => {
    if (isMobile && hasUserSelectedSearchBar) {
      setShowLandingPageText(false);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 300);
    }
    if (hasUserSelectedLocation) {
      setShowLandingPageText(false);
    }
    if (!hasUserSelectedLocation && !hasUserSelectedSearchBar) {
      setShowLandingPageText(true);
    }
  }, [hasUserSelectedLocation, hasUserSelectedSearchBar, isMobile]);

  React.useEffect(() => {
    const navEntries = performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];

    const navType = navEntries[0]?.type;
    const key = "cinemaSelectHardReloaded";

    if (navType !== "reload" && sessionStorage.getItem(key) !== "1") {
      sessionStorage.setItem(key, "1");
      window.location.reload();
    }

    setIsBootstrappingPage(false);
  }, []);

  if (isBootstrappingPage) return <LoadingElementInBox />;

  return (
    <>
      <VenueSelectorPageHead />
      <Container sx={{ width: { xs: "100%", md: "65%" } }}>
        <Collapse in={showLandingPageText} timeout={500}>
          <Box
            marginTop={{ xs: 3, md: 5 }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Typography
              variant="h1"
              textAlign="center"
              sx={{
                marginBottom: 2,
                fontSize: { xs: "large", md: "xx-large" },
                fontWeight: 500,
              }}
            >
              Enter your location to find film listings, showtimes, and cinemas
              near you:
            </Typography>
          </Box>
        </Collapse>
        <LocationSearchBar
          setLocation={handleLocationSelect}
          clearLocation={handleLocationClear}
          setIsSearchBarSelected={setHasUserSelectedSearchBar}
        />
        {loading ? (
          <LoadingElementInBox viewHeight="50vh" />
        ) : (
          venuesNearLocation.length > 0 && (
            <Box sx={{ ml: { xs: 0, md: 5 }, mr: { xs: 0, md: 5 }, py: 1 }}>
              <Typography
                variant="h2"
                textAlign="center"
                sx={{
                  fontSize: { xs: "large", md: "x-large" },
                  marginTop: 2,
                  marginBottom: 2,
                  padding: 1,
                  fontWeight: 500,
                }}
              >
                Select the cinemas you'd like to view showtimes for, then click
                view showtimes:
              </Typography>
              <VenueFormGroup
                venues={venuesNearLocation}
                selectedVenues={selectedVenues}
                onVenueToggle={handleVenueCheckboxClick}
                toggleSelectAllVenues={toggleSelectAllVenues}
                selectAllVenuesLabel="SELECT ALL VENUES NEAR YOU"
              />
            </Box>
          )
        )}
        {!loading &&
          isLocationCurrentlySelected &&
          venuesNearLocation.length > 0 && (
            <>
              <LinkToAboutPage />
              <WhatsOnButton
                venues={selectedVenues}
                isButtonDisabled={selectedVenues.length < 1}
              />
            </>
          )}
        {!loading &&
          isLocationCurrentlySelected &&
          venuesNearLocation.length === 0 && (
            <StandardTextBlock
              text="There are no cinemas near here to display. Please try another location."
              margin={2}
              padding={2}
            />
          )}
      </Container>
    </>
  );
};
